import React, { useEffect, useMemo, useState } from 'react';
import { makeAwsSignedRequest } from '../services/ApiService';
import NumberFormatter from '../utils/NumberFormatter';
import { EmptyState, ErrorIndicator, LoadingIndicator } from './common/LoadingStates';
import './TrackerTrendPanel.scss';

interface TrackerTrendPanelProps {
    stage: string;
    realm: string;
    /** Optional custom date range (slider). When set, overrides the coarse timeWindow for the trend series. */
    startDateTime?: Date;
    endDateTime?: Date;
    trackerData: {
        timeWindow?: string;
        clientId?: string;
        metadata?: {
            generatedAt?: string;
            [key: string]: any;
        };
    } | null;
}

interface TrackerTrendPoint {
    timestamp: string;
    created: number;
    approved: number;
    declined: number;
    pending: number;
    batched: number;
    unbatched: number;
    unstarted: number;
    withinSla: number;
    overSla: number;
    refundsPending: number;
    totalRefundsCreated: number;
}

interface TrackerTrendResponse {
    generatedAt: string;
    timeWindow: string;
    clientId: string;
    granularity: 'intraday' | 'daily' | string;
    windowStart?: string | null;
    windowEnd?: string | null;
    snapshotsAnalyzed: number;
    series: TrackerTrendPoint[];
}

type MetricKey = keyof Omit<TrackerTrendPoint, 'timestamp'>;

interface MetricDefinition {
    key: MetricKey;
    label: string;
    color: string;
}

const PRIMARY_METRICS: MetricDefinition[] = [
    { key: 'overSla', label: 'Stuck', color: '#a63a50' },
    { key: 'approved', label: 'Approved', color: '#0f766e' },
    { key: 'declined', label: 'Declined', color: '#d97706' },
    { key: 'withinSla', label: 'Within SLA', color: '#2563eb' },
];

const BREAKDOWN_METRICS: MetricDefinition[] = [
    { key: 'batched', label: 'Pending With Processor', color: '#7c3aed' },
    { key: 'unbatched', label: 'Pending On Platform', color: '#0f766e' },
    { key: 'unstarted', label: 'Not Started', color: '#dc2626' },
];

const parseNumber = (value: unknown): number => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    return 0;
};

const normalizeTrendResponse = (value: any): TrackerTrendResponse | null => {
    if (!value || typeof value !== 'object' || !Array.isArray(value.series)) {
        return null;
    }

    const series = value.series
        .filter((point: any) => point && typeof point.timestamp === 'string')
        .map((point: any) => ({
            timestamp: point.timestamp,
            created: parseNumber(point.created),
            approved: parseNumber(point.approved),
            declined: parseNumber(point.declined),
            pending: parseNumber(point.pending),
            batched: parseNumber(point.batched),
            unbatched: parseNumber(point.unbatched),
            unstarted: parseNumber(point.unstarted),
            withinSla: parseNumber(point.withinSla),
            overSla: parseNumber(point.overSla),
            refundsPending: parseNumber(point.refundsPending),
            totalRefundsCreated: parseNumber(point.totalRefundsCreated),
        }));

    return {
        generatedAt: typeof value.generatedAt === 'string' ? value.generatedAt : new Date().toISOString(),
        timeWindow: typeof value.timeWindow === 'string' ? value.timeWindow : 'now-30d',
        clientId: typeof value.clientId === 'string' ? value.clientId : 'AllClients',
        granularity: typeof value.granularity === 'string' ? value.granularity : 'daily',
        windowStart: typeof value.windowStart === 'string' ? value.windowStart : null,
        windowEnd: typeof value.windowEnd === 'string' ? value.windowEnd : null,
        snapshotsAnalyzed: parseNumber(value.snapshotsAnalyzed),
        series,
    };
};

const formatAxisLabel = (timestamp: string, granularity: string): string => {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
        return timestamp;
    }

    if (granularity === 'intraday') {
        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: false,
            timeZone: 'UTC',
        }).format(date);
    }

    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
    }).format(date);
};

const formatDelta = (delta: number): string => {
    if (delta === 0) {
        return 'No change';
    }
    return `${delta > 0 ? '+' : ''}${delta.toLocaleString('en-US')} vs window start`;
};

const formatWindowText = (start?: string | null, end?: string | null, fallbackWindow?: string): string => {
    if (start && end) {
        const startDate = new Date(start);
        const endDate = new Date(end);
        if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())) {
            const formatter = new Intl.DateTimeFormat('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
                timeZone: 'UTC',
            });
            return `${formatter.format(startDate)} to ${formatter.format(endDate)} UTC`;
        }

        return `${start} to ${end}`;
    }

    if (!fallbackWindow) {
        return 'Selected window';
    }

    const normalized = fallbackWindow.replace(/^now-/, 'Last ');
    return normalized.endsWith('h') || normalized.endsWith('d') ? normalized : fallbackWindow;
};

const formatScaleValue = (value: number): string => {
    if (value >= 1000) {
        return Intl.NumberFormat('en-US', {
            notation: 'compact',
            maximumFractionDigits: 1,
        }).format(value);
    }

    return value.toLocaleString('en-US');
};

const buildLinePath = (
    points: TrackerTrendPoint[],
    key: MetricKey,
    maxValue: number,
    width: number,
    height: number,
    paddingX: number,
    paddingY: number,
): string => {
    if (points.length === 0) {
        return '';
    }

    const innerWidth = width - paddingX * 2;
    const innerHeight = height - paddingY * 2;
    const denominator = Math.max(points.length - 1, 1);

    return points.map((point, index) => {
        const x = paddingX + (innerWidth * index) / denominator;
        const normalized = maxValue === 0 ? 0 : point[key] / maxValue;
        const y = paddingY + innerHeight - normalized * innerHeight;
        return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    }).join(' ');
};

const TrendChart: React.FC<{
    title: string;
    subtitle: string;
    points: TrackerTrendPoint[];
    metrics: MetricDefinition[];
    granularity: string;
}> = ({ title, subtitle, points, metrics, granularity }) => {
    const width = 860;
    const height = 280;
    const paddingX = 58;
    const paddingY = 24;
    const maxValue = points.reduce((currentMax, point) => {
        const pointMax = metrics.reduce((metricMax, metric) => Math.max(metricMax, point[metric.key]), 0);
        return Math.max(currentMax, pointMax);
    }, 0);
    const tickIndexes = Array.from(new Set([
        0,
        Math.max(0, Math.floor((points.length - 1) / 2)),
        Math.max(0, points.length - 1),
    ])).filter((index) => index < points.length);
    const yTickRatios = [0, 0.25, 0.5, 0.75, 1];
    const scaleMidpoint = Math.round(maxValue / 2);

    return (
        <div className="tracker-trend-chart-card">
            <div className="chart-copy">
                <div>
                    <h3>{title}</h3>
                    <p>{subtitle}</p>
                </div>
                <div className="tracker-trend-legend">
                    {metrics.map((metric) => (
                        <span key={metric.key} className="legend-chip">
                            <span className="legend-swatch" style={{ backgroundColor: metric.color }} />
                            {metric.label}
                        </span>
                    ))}
                </div>
            </div>
            <div className="chart-scale" aria-label={`${title} scale`}>
                <span className="chart-scale__label">Scale</span>
                <div className="chart-scale__values">
                    <span>Max {formatScaleValue(maxValue)}</span>
                    <span>Mid {formatScaleValue(scaleMidpoint)}</span>
                    <span>Min 0</span>
                </div>
            </div>
            <div className="chart-frame">
                <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={title}>
                    <text className="chart-axis-label" x="18" y={height / 2} transform={`rotate(-90 18 ${height / 2})`}>
                        Refund count
                    </text>
                    <g className="chart-grid">
                        {yTickRatios.map((ratio) => {
                            const y = paddingY + (height - paddingY * 2) * ratio;
                            return <line key={ratio} x1={paddingX} y1={y} x2={width - paddingX} y2={y} />;
                        })}
                    </g>
                    {yTickRatios.map((ratio) => {
                        const y = paddingY + (height - paddingY * 2) * ratio;
                        const tickValue = Math.round(maxValue * (1 - ratio));

                        return (
                            <g key={`y-${ratio}`} className="chart-y-tick">
                                <line x1={paddingX - 6} y1={y} x2={paddingX} y2={y} />
                                <text x={paddingX - 10} y={y + 4} textAnchor="end">
                                    {formatScaleValue(tickValue)}
                                </text>
                            </g>
                        );
                    })}
                    {metrics.map((metric) => (
                        <path
                            key={metric.key}
                            d={buildLinePath(points, metric.key, maxValue, width, height, paddingX, paddingY)}
                            fill="none"
                            stroke={metric.color}
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    ))}
                    {tickIndexes.map((index) => {
                        const denominator = Math.max(points.length - 1, 1);
                        const x = paddingX + ((width - paddingX * 2) * index) / denominator;
                        return (
                            <g key={index} className="chart-tick">
                                <line x1={x} y1={height - paddingY + 4} x2={x} y2={height - paddingY + 10} />
                                <text x={x} y={height - 2} textAnchor={index === 0 ? 'start' : index === points.length - 1 ? 'end' : 'middle'}>
                                    {formatAxisLabel(points[index].timestamp, granularity)}
                                </text>
                            </g>
                        );
                    })}
                </svg>
            </div>
        </div>
    );
};

const TrackerTrendPanel: React.FC<TrackerTrendPanelProps> = ({ stage, realm, trackerData, startDateTime, endDateTime }) => {
    const [trendData, setTrendData] = useState<TrackerTrendResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!trackerData?.timeWindow || !trackerData?.clientId) {
            setTrendData(null);
            setError(null);
            return;
        }

        let active = true;
        const controller = new AbortController();

        const loadTrendData = async () => {
            setLoading(true);
            setError(null);
            try {
                const params = new URLSearchParams({
                    timeWindow: trackerData.timeWindow || 'now-30d',
                    clientId: trackerData.clientId || 'AllClients',
                });
                if (startDateTime) {
                    params.set('startTime', startDateTime.toISOString());
                }
                if (endDateTime) {
                    params.set('endTime', endDateTime.toISOString());
                }
                const data = await makeAwsSignedRequest(stage, realm, `tracker/trends?${params.toString()}`, {
                    signal: controller.signal,
                    timeoutMs: 120000,
                });
                const normalized = normalizeTrendResponse(data);
                if (!normalized) {
                    throw new Error('Tracker trend API returned an unexpected payload shape');
                }
                if (active) {
                    setTrendData(normalized);
                }
            } catch (err) {
                if (!active || controller.signal.aborted) {
                    return;
                }
                setTrendData(null);
                setError(err instanceof Error ? err.message : 'Failed to load tracker trends');
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        loadTrendData();

        return () => {
            active = false;
            controller.abort();
        };
    }, [stage, realm, trackerData?.timeWindow, trackerData?.clientId, trackerData?.metadata?.generatedAt,
        startDateTime?.getTime(), endDateTime?.getTime()]);

    const summaryMetrics = useMemo(() => {
        if (!trendData?.series?.length) {
            return [];
        }

        const first = trendData.series[0];
        const last = trendData.series[trendData.series.length - 1];
        return PRIMARY_METRICS.map((metric) => ({
            ...metric,
            current: last[metric.key],
            delta: last[metric.key] - first[metric.key],
        }));
    }, [trendData]);

    if (!trackerData) {
        return null;
    }

    return (
        <section className="tracker-trend-panel">
            <div className="tracker-trend-panel__header">
                <div>
                    <h2>Refund trends</h2>
                </div>
                {trendData && (
                    <div className="tracker-trend-panel__meta">
                        <span>{formatWindowText(trendData.windowStart, trendData.windowEnd, trendData.timeWindow)}</span>
                    </div>
                )}
            </div>

            {loading && <LoadingIndicator message="Loading refund trends..." />}
            {!loading && error && <ErrorIndicator error={error} />}

            {!loading && !error && trendData && trendData.series.length === 0 && (
                <EmptyState
                    title="Trend history is building"
                    message="Trend data for this window is still building. Refresh again shortly."
                />
            )}

            {!loading && !error && trendData?.series?.length ? (
                <>
                    <div className="tracker-trend-summary-grid">
                        {summaryMetrics.map((metric) => (
                            <div key={metric.key} className="tracker-trend-summary-card">
                                <div className="summary-label-row">
                                    <span className="summary-swatch" style={{ backgroundColor: metric.color }} />
                                    <span className="summary-label">{metric.label}</span>
                                </div>
                                <div className="summary-value"><NumberFormatter number={metric.current} /></div>
                                <div className={`summary-delta ${metric.delta > 0 ? 'up' : metric.delta < 0 ? 'down' : 'flat'}`}>
                                    {formatDelta(metric.delta)}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="tracker-trend-chart-grid">
                        <TrendChart
                            title="Refund state history"
                            subtitle="Pending, approved, declined, and within-SLA counts across the selected window."
                            points={trendData.series}
                            metrics={PRIMARY_METRICS}
                            granularity={trendData.granularity}
                        />
                        <TrendChart
                            title="Stuck refunds composition"
                            subtitle="How the stuck backlog split between processor, platform, and not-started buckets."
                            points={trendData.series}
                            metrics={BREAKDOWN_METRICS}
                            granularity={trendData.granularity}
                        />
                    </div>
                </>
            ) : null}
        </section>
    );
};

export default TrackerTrendPanel;
